import { useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Settings2, Plus, Trash2, Wifi } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { isSubOrSuperAdmin, type UserRole } from '@/lib/constants/roles';
import { cn } from '@/lib/utils/cn';
import {
  useDeletePlan,
  useToggleVoicePlanStatus,
  useVoicePlans,
} from '../api';
import { AddVoicePlanSheet } from '../components/AddVoicePlanSheet';
import type { VoicePlanRow } from '../types';

export function VoicePlanListPage() {
  const role = useAuthStore((s) => s.role) as UserRole;
  const showOwner = isSubOrSuperAdmin(role);

  const list = useVoicePlans();
  const rows = list.data ?? [];

  const toggleStatus = useToggleVoicePlanStatus();
  const del = useDeletePlan();

  const [pendingDelete, setPendingDelete] = useState<VoicePlanRow | null>(
    null,
  );
  const [addOpen, setAddOpen] = useState(false);

  const columns = useMemo<ColumnDef<VoicePlanRow>[]>(
    () => [
      {
        accessorKey: 'planId',
        header: 'ID',
        size: 70,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.planId}
          </span>
        ),
      },
      {
        accessorKey: 'planName',
        header: 'Plan',
        cell: ({ row }) => (
          <span className="flex items-center gap-2 font-medium">
            <Wifi className="h-3.5 w-3.5 text-muted-foreground" />
            {row.original.planName}
          </span>
        ),
      },
      {
        accessorKey: 'pulseDuration',
        header: 'Pulse',
        size: 110,
        cell: ({ row }) => (
          <Badge variant="outline" className="font-normal">
            {row.original.pulseDuration}
          </Badge>
        ),
      },
      {
        accessorKey: 'pulsePrice',
        header: 'Price',
        size: 110,
        cell: ({ row }) => (
          <span className="font-mono tabular-nums">
            ₹{(Number(row.original.pulsePrice) / 100).toFixed(2)}{' '}
            <span className="text-[10px] text-muted-foreground">
              ({row.original.pulsePrice}p)
            </span>
          </span>
        ),
      },
      ...(showOwner
        ? [
            {
              accessorKey: 'username',
              header: 'Created by',
              size: 130,
              cell: ({ row }: { row: { original: VoicePlanRow } }) =>
                row.original.username ?? '—',
            } as ColumnDef<VoicePlanRow>,
          ]
        : []),
      {
        accessorKey: 'planStatus',
        header: 'Status',
        size: 130,
        enableSorting: false,
        cell: ({ row }) => {
          const enabled = row.original.planStatus === 1;
          return (
            <div className="flex items-center gap-2">
              <Switch
                checked={enabled}
                onCheckedChange={(next) =>
                  toggleStatus.mutate(
                    {
                      planId: row.original.planId,
                      planStatus: next ? 1 : 0,
                    },
                    {
                      onSuccess: (res) =>
                        toast.success(
                          res?.message ??
                            (next ? 'Plan enabled' : 'Plan disabled'),
                        ),
                      onError: () => toast.error('Could not update status.'),
                    },
                  )
                }
                aria-label={enabled ? 'Disable plan' : 'Enable plan'}
              />
              <span
                className={cn(
                  'text-xs font-medium',
                  enabled ? 'text-emerald-700' : 'text-muted-foreground',
                )}
              >
                {enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: 'Settings',
        size: 80,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <Settings2 className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuLabel>{row.original.planName}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setPendingDelete(row.original)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [showOwner, toggleStatus],
  );

  return (
    <FillPage>
      <PageHeader
        title="Voice Pricing"
        description="Pulse-based pricing tiers you can assign to end users."
        actions={
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4" />
            New plan
          </Button>
        }
      />

      <DataTable<VoicePlanRow, unknown>
        tableId="plans:voice:list:v1"
        columns={columns}
        data={rows}
        isLoading={list.isLoading}
        searchPlaceholder="Search plans…"
        emptyTitle="No voice plans"
        emptyDescription='Click "New plan" to define your first pricing tier.'
        getRowId={(row) => String(row.planId)}
        initialColumnPinning={{ right: ['actions'] }}
        exportFilename="voice-plans.csv"
        pageSize={25}
      />

      <AddVoicePlanSheet open={addOpen} onOpenChange={setAddOpen} />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete voice plan?"
        description={
          <>
            Plan{' '}
            <span className="font-medium text-foreground">
              {pendingDelete?.planName}
            </span>{' '}
            will be permanently removed. Users currently on this plan keep
            their existing assignment until you reassign them.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={del.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          del.mutate(pendingDelete.planId, {
            onSuccess: (res) => {
              toast.success(res?.message ?? `Deleted ${pendingDelete.planName}`);
              setPendingDelete(null);
            },
            onError: () => toast.error('Could not delete the plan.'),
          });
        }}
      />
    </FillPage>
  );
}
